import { createColumnHelper } from "@tanstack/react-table";
import { PaneBody } from "@/components/pane/paneBody.tsx";
import { IColumn } from "@/domain/interfaces/sfTable.interface.ts";
import { isNil } from "lodash";
import {
  ICascadeLevelDetails,
  useCascadeLevelsWindow,
} from "@/hooks/useCascadeLevelsWindow.ts";
import { usePaneContext } from "@/components/pane/paneContext.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { PaneSubHeader } from "@/components/pane/paneSubHeader.tsx";
import React, { useMemo } from "react";
import { BINANCE_RESOLUTION_MAP } from "@/lib/datafeed/config.ts";
import { applyFilters, cn } from "@/lib/utils.ts";
import { HighlightedValueChange } from "@/components/shared/highlightedValueChange.tsx";
import { Loader } from "@/components/shared/loader.tsx";
import { useDashboard } from "@/lib/store";
import { useFilterData } from "@/hooks/useFilterData";

const columnHelper = createColumnHelper<ICascadeLevelDetails>();

const columns = [
  columnHelper.accessor("symbol", {
    header: "Symbol",
    enableSorting: false,
  }),
  columnHelper.accessor("toFcPrc", {
    header: "Dist",
    cell: (info) => {
      const value = info.getValue();
      const side = info.row.original.side;
      const bgClass = getBgClass(side);
      if (isNil(value)) {
        return <div>-</div>;
      }
      return (
        <HighlightedValueChange
          value={value}
          highlightNewTextColor={false}
          highlightPosNeg={false}
          className={cn(bgClass, "font-bold block px-0.5")}
          decimal={2}
        />
      );
    },
    size: 50,
    minSize: 20,
  }),
  columnHelper.accessor("daysFromNow", {
    header: "Time",
    size: 50,
    minSize: 50,
  }),
  columnHelper.accessor("fcSum", {
    header: "Calls",
    size: 50,
    minSize: 20,
  }),
];

const getBgClass = (side: string) => {
  return side === "UP" ? "bg-[#B2182C]" : "bg-[#3CA64B]";
};

export function CascadeLevelsPaneBody() {
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);

  const config = getNodeConfig();

  const { data: cascadeLevels, isLoading } = useCascadeLevelsWindow(
    BINANCE_RESOLUTION_MAP[config.interval],
  );

  const filteredData = useFilterData<ICascadeLevelDetails>(cascadeLevels || []);
  const { globalFilters, getGlobalFilterValues, blacklist } = useDashboard();

  const preparedData = useMemo(() => {
    if (!filteredData || isLoading) {
      return [];
    }
    let preparedData = applyFilters(
      filteredData,
      globalFilters,
      getGlobalFilterValues(),
    );

    if (blacklist.length > 0) {
      blacklist.forEach((symbol) => {
        preparedData = preparedData.filter((item) => item.symbol !== symbol);
      });
    }

    if (config?.filters && config?.filterValues) {
      return applyFilters<ICascadeLevelDetails>(
        preparedData,
        config.filters,
        config.filterValues,
      );
    }

    return preparedData;
  }, [
    filteredData,
    isLoading,
    globalFilters,
    getGlobalFilterValues,
    blacklist,
    config.filters,
    config.filterValues,
  ]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <PaneSubHeader />
      <PaneBody
        className="!h-[calc(100%-24px)]"
        tableColumns={columns as IColumn[]}
        data={preparedData ?? []}
      />
    </>
  );
}
